import {
  useRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Pagination, Table, Button as RsButton } from "rsuite";
import { useHistory } from "react-router-dom";
import { AppRouteApi, AppRouteUi } from "../../config/appRoute";
import { useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { PaginatedData } from "../../models/api/paginatedData";
import { ActionsUsersTable } from "../../core/redux/slices/sliceUsersTable";
import { ActionCell, TextCell } from "../../components/tableItem.component";
import { ApiSchemaUser, ApiSchemaUserPaginated } from "../../models/api";
import { ApiUsers } from "../../core/apis/users";
import { useAlertDialog } from "../../components/alertDialog";
import { ApiAuth } from "../../core/apis/auth";
import { sortTableData } from "../../utils/tableFilter";
import {
  useTablePaginationSearchFilter,
  IPaginationSearchFilter,
} from "../../hooks";
import { TableHeader } from "../../components";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaUser & {
  sl: number;
  checked?: boolean;
  name?: string;
};

export const UsersList = () => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [selected, setSelected] = useState<TableData[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const triggerRef = useRef<any>();
  const routerHistory = useHistory();
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const {
    pageNumber,
    pageSize,
    isLoading,
    searchKey,
    nextPageToken,
    shouldRefetch,
  } = useAppSelector((state) => state.usersTable);
  const dispatch = useAppDispatch();
  const customParams = useMemo<IPaginationSearchFilter["customParams"]>(() => {
    return {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SearchKey: searchKey,
      NextPageToken: nextPageToken,
    };
  }, [pageNumber, pageSize, searchKey, nextPageToken]);
  const { apiData, refetch } = useTablePaginationSearchFilter<
    ApiSchemaUserPaginated,
    typeof ActionsUsersTable
  >(ActionsUsersTable, {
    customParams,
    apiRoute: AppRouteApi.Users.Root(),
  });

  const [sortBy, setSortBy] = useState<keyof TableData>("sl");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(
    () =>
      setTableData(
        apiData?.list.map((user, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          name: user.firstName + " " + user.lastName,
          ...user,
        })) ?? []
      ),
    [apiData, pageNumber, pageSize]
  );

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [sortAsc, sortBy, tableData]);

  const CellRowSelection = useMemo((): ReactNode => {
    return (rowData: TableData) => (
      <input
        type="checkbox"
        checked={!!rowData.checked}
        onChange={(ev) =>
          rowData.sKey &&
          setTableData((tableData) =>
            tableData?.map((td) =>
              td.sKey === rowData.sKey
                ? {
                    ...td,
                    checked: ev.target.checked,
                  }
                : td
            )
          )
        }
      />
    );
  }, []);

  const selectAllRow = useCallback((checked: boolean) => {
    setTableData((tableData) =>
      tableData.map((td) => ({
        ...td,
        checked: checked,
      }))
    );
  }, []);

  const isAllChecked = useMemo(
    () => tableData.length > 0 && tableData.every((td) => !!td.checked),
    [tableData]
  );

  useEffect(() => {
    setSelected(tableData.filter((td) => !!td.checked));
  }, [tableData]);

  const updateDeleted =
    (sKeys: string[]) => async (res?: PaginatedData<ApiSchemaUser>) => {
      let updated: PaginatedData<ApiSchemaUser> = {
        totalCount: res?.totalCount || 0,
        list: res?.list.filter((i) => !sKeys.includes(i.sKey)) || [],
      };
      return updated;
    };
  const onDelete = useCallback(
    () =>
      alertDialogCtrl.open({
        message: "Are you sure you want to delete the selected Users?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          setIsDeleting(true);
          dispatch(ActionsUsersTable.setIsLoading(true));
          const selectedIds = selected.filter((s) => s.sKey).map((s) => s.sKey);
          await deleteItem(selectedIds);
          const sKeys = selected.map((item) => item.sKey);
          ApiUsers.GetAll.Mutate(updateDeleted(sKeys));
          setIsDeleting(false);
          dispatch(ActionsUsersTable.setIsLoading(false));
          dispatch(ActionsUsersTable.setRefetch(true));
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );
  const handleSelectMenu = async (
    eventKey: string,
    event: Event,
    sKey?: string
  ) => {
    if (eventKey === "delete" && sKey)
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this User?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsUsersTable.setIsLoading(true));
          const deleted = await deleteItem([sKey]);
          if (deleted) ApiUsers.GetAll.Mutate(updateDeleted([sKey]));
          dispatch(ActionsUsersTable.setIsLoading(false));
        },
      });
    else if (eventKey === "update" && sKey)
      routerHistory.push(AppRouteUi.Users.UpdateById(sKey));
  };

  const deleteItem = async (sKey: string[]) => await ApiUsers.Delete(sKey);

  useEffect(() => {
    shouldRefetch && refetch();
  }, [shouldRefetch, refetch]);

  return (
    <div>
      {alertDialogComponent}
      <TableHeader
        buttonText="Add New Users"
        redirectPath={AppRouteUi.Users.Add()}
        onDelete={onDelete}
        pageSize={pageSize}
        TableAction={ActionsUsersTable}
        isDeleting={isDeleting}
        isSelected={!!selected.length}
      />
      <Table
        autoHeight
        data={filteredTableData}
        loading={isLoading}
        onScroll={() => triggerRef.current?.close()}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column align="center" width={35}>
          <HeaderCell className="fw-bold text-black">
            <input
              type="checkbox"
              checked={isAllChecked}
              onChange={(ev) => selectAllRow(ev.target.checked)}
            />
          </HeaderCell>
          <Cell>{CellRowSelection}</Cell>
        </Column>
        <Column align="center" width={70} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="S/L">S/L</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="sl" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Name">Name</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="name" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Email">Email</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="email" />
        </Column>

        <Column width={200}>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone">Phone</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="phoneNumber" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone">Username</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="userName" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Phone">Company</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="companyName" />
        </Column>

        <Column width={200} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Department">Department</span>
          </HeaderCell>
          <TextCell component="Users" dataKey="department" />
        </Column>

        <Column align="center">
          <HeaderCell className="fw-bold text-black"></HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <RsButton
                size="xs"
                appearance="primary"
                disabled={rowData.accountStatus === "CONFIRMED"}
                loading={rowData.accountStatus === "LOADING"}
                onClick={() => {
                  alertDialogCtrl.open({
                    message:
                      "Once you activate this user, can't be reverted. Are you sure to activate this user?",
                    proceedBtnText: "Activate",
                    type: "warning",
                    onSuccess: async () => {
                      setTableData(
                        tableData.map((data) =>
                          data.sKey !== rowData?.sKey
                            ? data
                            : { ...data, accountStatus: "LOADING" }
                        )
                      );
                      const isSuccess = await ApiAuth.ActivateUser(
                        rowData.userName as string
                      );
                      setTableData(
                        tableData.map((data) =>
                          data.sKey !== rowData?.sKey
                            ? data
                            : {
                                ...data,
                                accountStatus: isSuccess
                                  ? "CONFIRMED"
                                  : "UNCONFIRMED",
                              }
                        )
                      );
                    },
                  });
                }}
              >
                {rowData.accountStatus === "CONFIRMED"
                  ? "Activated"
                  : "Activate"}
              </RsButton>
            )}
          </Cell>
        </Column>

        <Column align="center" width={70} fixed="right">
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <ActionCell
            component="Users"
            handleSelectMenu={handleSelectMenu}
            triggerRef={triggerRef}
          />
        </Column>
      </Table>

      {apiData?.list && (
        <div className="d-flex justify-content-end mt-3">
          <Pagination
            total={apiData?.list.length}
            pages={Math.ceil(apiData?.totalCount / pageSize)}
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) => dispatch(ActionsUsersTable.goToPage(page))}
          />
        </div>
      )}
    </div>
  );
};
